import {
  getFilterDefinition,
  MULTI_FILTER_ID_BY_FILTER_ID,
} from '../../engine/filterRegistry';
import {
  type Filter,
  type FilterId,
  FilterType,
  type FreeTextFilter,
  type ListFilter,
  type MultiFilter,
  type RangeFilter,
  type SchoolFilter,
  type StaticListFilter,
  type TreeFilter,
} from '../../types/filters';

/**
 * CustomDash represents "multiple chips for the same logical filter" via a MULTI_* wrapper
 * filter. This file centralizes that wrapping logic.
 *
 * Example:
 * - Role filter instances: [ {Role: Engineer}, {Role: Scientist} ]
 * - Output: MULTI_ROLE { instances: [...] }
 */

const isWrappableFilter = (
  filter: Filter
): filter is
  | TreeFilter
  | ListFilter
  | StaticListFilter
  | FreeTextFilter
  | RangeFilter
  | SchoolFilter => {
  return (
    filter.type === FilterType.TREE ||
    filter.type === FilterType.LIST ||
    filter.type === FilterType.STATIC_LIST ||
    filter.type === FilterType.FREE_TEXT ||
    filter.type === FilterType.RANGE ||
    filter.type === FilterType.SCHOOL
  );
};

const createNewMultiFilter = <
  T extends
    | TreeFilter
    | ListFilter
    | StaticListFilter
    | FreeTextFilter
    | RangeFilter
    | SchoolFilter,
>(
  filter: T,
  multiFilterId: FilterId,
  name?: string
): MultiFilter<T> => ({
  id: multiFilterId,
  name: name ?? filter.name,
  type: FilterType.MULTI,
  wrappedFilterId: filter.id,
  instances: [filter],
  isVisible: true,
  isUserSubmitted: true,
});

const wrapFilterInMultiIfNeeded = (filter: Filter): Filter => {
  // Some filters are always displayed/managed as MULTI_* in the UI. If we got a single instance,
  // we still wrap it so the rest of the app can treat it uniformly.
  if (filter.type === FilterType.MULTI) {
    return filter;
  }

  const multiFilterId = MULTI_FILTER_ID_BY_FILTER_ID[filter.id];
  if (!multiFilterId) {
    return filter;
  }

  const definition = getFilterDefinition(multiFilterId);
  if (!isWrappableFilter(filter)) {
    return filter;
  }

  return createNewMultiFilter(
    filter,
    multiFilterId,
    definition?.name
  ) as Filter;
};

export const toMultiOrSingle = <T extends Filter>(
  baseFilterId: FilterId,
  instances: T[]
): Filter[] => {
  // Convert the accumulated instances for a base filter into the final Filter union.
  if (instances.length === 0) return [];

  const multiFilterId = MULTI_FILTER_ID_BY_FILTER_ID[baseFilterId];
  if (!multiFilterId) {
    return instances[0] ? [instances[0] as Filter] : [];
  }

  if (instances.length === 1) {
    const first = instances[0];
    return first ? [wrapFilterInMultiIfNeeded(first as Filter)] : [];
  }

  const definition = getFilterDefinition(multiFilterId);
  const multiFilter: MultiFilter<T> = {
    id: multiFilterId,
    name: definition?.name ?? String(multiFilterId),
    type: FilterType.MULTI,
    wrappedFilterId: baseFilterId,
    instances,
    isVisible: true,
    isUserSubmitted: true,
  };

  // The `Filter` union is intentionally explicit (MultiFilter<TreeFilter> | MultiFilter<ListFilter> | ...),
  // and TS can't prove `MultiFilter<T>` is always one of those here. The runtime shape is correct.
  return [multiFilter as unknown as Filter];
};
